/**
 * Shared bits for the two GROUP vocabulary screens (platforms, stacks) — the
 * `states.tsx` idiom, one level up the permission ladder.
 *
 * `NoAdminAccess` names `config.manage` in its sentence, which is the right sentence
 * for every country-config screen and the WRONG one here: `/admin/platforms` and
 * `/admin/stacks` gate every route INCLUDING THE READ on `config.global`
 * (`platforms.controller.ts:64-69`, `stacks.controller.ts:58-63`, enforced by
 * `assertGlobalConfigAccess` — grp or the override-only key, never `config.manage`
 * however many countries it is held in). A refusal that told a Country Manager to
 * ask for `config.manage` would send them after a key they may already hold.
 */

import { ShieldCheck } from 'lucide-react';
import { EmptyState } from '../../components/ui';

/**
 * The client-side courtesy for a user who reached a GROUP vocabulary URL without
 * `config.global`. The API is the control (it answers 403 on the read itself —
 * `platforms.e2e-spec.ts` suite (b)); this buys an honest sentence instead of a
 * screen that renders and fills with refusals.
 */
export function NoGlobalConfigAccess(): React.JSX.Element {
  return (
    <div className="px-6 py-10">
      <EmptyState
        icon={<ShieldCheck aria-hidden="true" className="size-5" />}
        title="You do not have access to this screen"
        description="The group's shared vocabulary is a global object: editing or reading it here needs group admin or the config.global permission (owner decision D3). Ask your administrator if you believe you should have it."
      />
    </div>
  );
}

/**
 * Human labels for the SET's members — labels for the THREE-MEMBER SCHEMA CONSTANT
 * (`platform.entity.ts:40`), not a map from platform names to applicability. That
 * distinction is the whole of owner ruling Q8-b: the domain of the answer is schema,
 * the answer itself is data on each row.
 */
export const PLATFORM_TARGET_LABELS: Record<string, string> = {
  crf: 'CRFs',
  project: 'Projects',
  inquiry: 'Inquiries',
};

/** `['crf','project']` → `"CRFs, Projects"` — an unknown member renders as itself. */
export function appliesToSentence(appliesTo: readonly string[]): string {
  return appliesTo.map((target) => PLATFORM_TARGET_LABELS[target] ?? target).join(', ');
}
